// =============================================================================
// Webinar Session Registrations — Admin approval page
// =============================================================================
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { webinarSessionAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, XCircle, Clock, Mail, Phone, Building, User, Briefcase, MessageSquare, Trash2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';

export default function WebinarSessionRegistrationsPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    loadData();
  }, [sessionId]);

  const loadData = async () => {
    try {
      const [sessionRes, regsRes] = await Promise.all([
        webinarSessionAPI.get(sessionId),
        webinarSessionAPI.listRegistrations(sessionId),
      ]);
      setSession(sessionRes.data);
      setRegistrations(regsRes.data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (regId) => {
    setActionLoading(prev => ({ ...prev, [regId]: 'approve' }));
    try {
      await webinarSessionAPI.approveRegistration(sessionId, regId);
      toast.success('Inscription approuvée — Email envoyé au candidat');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(prev => ({ ...prev, [regId]: null }));
    }
  };

  const handleReject = async (regId) => {
    if (!window.confirm('Rejeter cette inscription ?')) return;
    setActionLoading(prev => ({ ...prev, [regId]: 'reject' }));
    try {
      await webinarSessionAPI.rejectRegistration(sessionId, regId);
      toast.success('Inscription rejetée');
      loadData();
    } catch {
      toast.error('Erreur');
    } finally {
      setActionLoading(prev => ({ ...prev, [regId]: null }));
    }
  };

  const handleDelete = async (regId) => {
    if (!window.confirm('Supprimer définitivement cette inscription ?')) return;
    setActionLoading(prev => ({ ...prev, [regId]: 'delete' }));
    try {
      await webinarSessionAPI.deleteRegistration(sessionId, regId);
      toast.success('Inscription supprimée');
      loadData();
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setActionLoading(prev => ({ ...prev, [regId]: null }));
    }
  };

  const handleResendEmail = async (regId) => {
    setActionLoading(prev => ({ ...prev, [regId]: 'resend' }));
    try {
      await webinarSessionAPI.resendEmail(sessionId, regId);
      toast.success('Email renvoyé avec succès');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du renvoi de l\'email');
    } finally {
      setActionLoading(prev => ({ ...prev, [regId]: null }));
    }
  };

  const statusConfig = {
    pending: { label: 'En attente', icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200' },
    approved: { label: 'Approuvé', icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
    rejected: { label: 'Rejeté', icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200' },
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const pendingCount = registrations.filter(r => r.status === 'pending').length;
  const approvedCount = registrations.filter(r => r.status === 'approved').length;

  return (
    <AppLayout>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/webinar-sessions')} className="text-zinc-500 hover:text-zinc-900">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">Inscriptions</h1>
            <p className="text-sm text-zinc-500">{session?.title}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-zinc-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-zinc-900">{registrations.length}</p>
            <p className="text-xs text-zinc-500">Total</p>
          </div>
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
            <p className="text-xs text-amber-600/80">En attente</p>
          </div>
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-emerald-600">{approvedCount}</p>
            <p className="text-xs text-emerald-600/80">Approuvés</p>
          </div>
        </div>

        {/* Registrations list */}
        {registrations.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-zinc-200 shadow-sm">
            <User className="w-12 h-12 text-zinc-400 mx-auto mb-3 opacity-50" />
            <p className="text-zinc-500">Aucune inscription pour le moment</p>
          </div>
        ) : (
          <div className="space-y-3">
            {registrations.map(reg => {
              const sc = statusConfig[reg.status] || statusConfig.pending;
              const StatusIcon = sc.icon;
              const isLoading = actionLoading[reg.id];

              return (
                <div key={reg.id} className="bg-white rounded-xl border border-zinc-200 p-5 hover:border-zinc-300 hover:shadow-md transition-all shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                          {reg.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-zinc-900">{reg.full_name}</h3>
                          <p className="text-xs text-zinc-500">{new Date(reg.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border flex items-center gap-1 ${sc.color}`}>
                          <StatusIcon className="w-3 h-3" /> {sc.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-xs text-zinc-600 ml-12">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-zinc-400" /> {reg.email}</span>
                        {reg.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-zinc-400" /> {reg.phone}</span>}
                        {reg.company && <span className="flex items-center gap-1"><Building className="w-3 h-3 text-zinc-400" /> {reg.company}</span>}
                        {reg.job_title && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3 text-zinc-400" /> {reg.job_title}</span>}
                      </div>
                      {reg.message && (
                        <p className="text-xs text-zinc-600 ml-12 mt-2 flex items-start gap-1">
                          <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-zinc-400" /> {reg.message}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      {reg.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(reg.id)}
                            disabled={!!isLoading}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 text-xs h-8"
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                            {isLoading === 'approve' ? '...' : 'Approuver'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleReject(reg.id)}
                            disabled={!!isLoading}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1 text-xs h-8"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            {isLoading === 'reject' ? '...' : 'Rejeter'}
                          </Button>
                        </>
                      )}
                      
                      {reg.status === 'approved' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResendEmail(reg.id)}
                          disabled={!!isLoading}
                          className="border-zinc-200 text-zinc-600 hover:bg-zinc-50 gap-1 text-xs h-8"
                          title="Renvoyer l'email d'accès"
                        >
                          <Send className="w-3.5 h-3.5" />
                          {isLoading === 'resend' ? '...' : 'Renvoyer l\'email'}
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(reg.id)}
                        disabled={!!isLoading}
                        className="text-zinc-400 hover:text-red-600 hover:bg-red-50 gap-1 text-xs h-8 px-2"
                        title="Supprimer l'inscription"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
